import { CoverAllLoad } from 'apps/sigma2-ui/src/components/CoverAllLoad';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { S2MonocoEditor } from 'apps/sigma2-ui/src/data-wizard/components/S2MonocoEditor';
import React from 'react';
import { PlatformTransformDetails } from 'libs/end-to-end/src/types/databases/transform/transform';
import { PlatformTransformEntrypoint } from 'libs/end-to-end/src/types/databases/transform/platform-transform-entrypoints';
import { PlatformTransformEntrypoints } from 'libs/end-to-end/src/types/databases/transform/platform-transform-entrypoints';
import { DagsterTransformSelector } from 'apps/sigma2-ui/src/data-wizard/components/DagsterTransformSelector';
import { OrganizationEntitledDagsterTransform } from 'libs/end-to-end/src/external-dagster-transforms';

export type TransformType = 'python' | 'platform' | 'dagster';

export const TransformEditorUI = ({
  code,
  setCode,
  isRunningExecution,
  isLoadingScript,
  setRunOnBatch,
  runOnBatch,
  transformType,
  setTransformType,
  isToBeCompared,
  setIsToBeCompared,
  platformTransformDetails,
  setPlatformTransformDetails,
  entitledDagsterTransforms,
  selectedDagsterTransformId,
  setSelectedDagsterTransformId,
}: {
  code: string;
  setCode: Setter<string>;
  isRunningExecution: boolean;
  isLoadingScript: boolean;
  runOnBatch: boolean;
  setRunOnBatch: Setter<boolean>;
  transformType: TransformType;
  setTransformType: Setter<TransformType>;
  isToBeCompared: boolean;
  setIsToBeCompared: Setter<boolean>;
  platformTransformDetails?: PlatformTransformDetails | null;
  setPlatformTransformDetails: Setter<PlatformTransformDetails | null>;
  entitledDagsterTransforms: OrganizationEntitledDagsterTransform[];
  selectedDagsterTransformId: string | null;
  setSelectedDagsterTransformId: Setter<string | null>;
}) => {
  const emptyState = 'none';
  const transformEntrypointOptions: string[] = [
    emptyState,
    ...PlatformTransformEntrypoints,
  ];

  return (
    <div className={'relative'}>
      {/* Transform Type Selector */}
      <div className="mb-4 flex gap-6">
        <label className="flex items-center cursor-pointer">
          <input
            type="radio"
            value="python"
            checked={transformType === 'python'}
            onChange={(e) => setTransformType(e.target.value as TransformType)}
            disabled={isRunningExecution || isLoadingScript}
            className="mr-2 form-radio"
            data-qa="transformTypePython"
          />
          <span className="text-sm font-medium text-gray-700">
            Python Script
          </span>
        </label>
        <label className="flex items-center cursor-pointer">
          <input
            type="radio"
            value="platform"
            checked={transformType === 'platform'}
            onChange={(e) => setTransformType(e.target.value as TransformType)}
            disabled={isRunningExecution || isLoadingScript}
            className="mr-2 form-radio"
            data-qa="transformTypePlatform"
          />
          <span className="text-sm font-medium text-gray-700">
            Platform Transform
          </span>
        </label>
        <label className="flex items-center cursor-pointer">
          <input
            type="radio"
            value="dagster"
            checked={transformType === 'dagster'}
            onChange={(e) => setTransformType(e.target.value as TransformType)}
            disabled={isRunningExecution || isLoadingScript}
            className="mr-2 form-radio"
            data-qa="transformTypeDagster"
          />
          <span className="text-sm font-medium text-gray-700">
            Dagster Transform
          </span>
        </label>
      </div>

      {/* Conditional Editor Based on Transform Type */}
      {transformType === 'python' && (
        <S2MonocoEditor
          language="python"
          disabled={isRunningExecution || isLoadingScript}
          code={code}
          setCode={setCode}
        />
      )}

      {transformType === 'platform' && (
        <label className="block text-sm font-medium text-gray-700">
          Platform Transform Entrypoint{' '}
          <select
            className="form-select"
            value={platformTransformDetails?.transformEntrypoint ?? emptyState}
            onChange={(e) => {
              const parsedInput = PlatformTransformEntrypoint.safeParse(
                e.target.value,
              );
              if (parsedInput.success) {
                setPlatformTransformDetails({
                  transformEntrypoint: parsedInput.data,
                });
              } else {
                setPlatformTransformDetails(null);
              }
            }}
          >
            {transformEntrypointOptions.map((entrypoint) => (
              <option key={entrypoint} value={entrypoint}>
                {entrypoint}
              </option>
            ))}
          </select>
        </label>
      )}

      {transformType === 'dagster' && (
        <DagsterTransformSelector
          entitledTransforms={entitledDagsterTransforms}
          selectedTransformId={selectedDagsterTransformId}
          setSelectedTransformId={setSelectedDagsterTransformId}
          isDisabled={isRunningExecution || isLoadingScript}
        />
      )}

      <div className="flex items-center mt-2">
        <S2Checkbox
          onChange={() => {
            setRunOnBatch(!runOnBatch);
          }}
          checked={runOnBatch}
        >
          Run on batch
        </S2Checkbox>
        <div className="w-4"></div>
        <S2Checkbox
          onChange={() => {
            setIsToBeCompared(!isToBeCompared);
          }}
          checked={isToBeCompared}
        >
          Compare With Previous Transform
        </S2Checkbox>
      </div>
      {(isRunningExecution || isLoadingScript) && <CoverAllLoad />}
    </div>
  );
};
